import { useEffect, useState } from "react";
import type { Game } from "@engine/core/Game";
import type { Entity } from "@engine/ecs/components";

interface Props {
  game?: Game | undefined;
}

const SAMPLE_INTERVAL_MS = 200;

export function InspectorPanel({ game }: Props) {
  const [selected, setSelected] = useState<Entity | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!game) {
      setSelected(null);
      return;
    }
    setSelected(game.inspector.current());
    const unsub = game.inspector.subscribe((e) => setSelected(e));
    const tickerId = window.setInterval(() => setTick((t) => t + 1), SAMPLE_INTERVAL_MS);
    return () => {
      unsub();
      window.clearInterval(tickerId);
    };
  }, [game]);

  if (!selected) return null;

  return (
    <div
      data-testid="inspector-panel"
      style={{
        position: "fixed",
        top: 8,
        right: 8,
        width: 280,
        padding: "8px 10px",
        background: "rgba(0,0,0,0.6)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.4,
        color: "#e6e8ee",
        fontFamily: "ui-monospace, monospace",
        zIndex: 1000,
        maxHeight: "70vh",
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>Inspector</strong>
        <button
          type="button"
          onClick={() => game?.inspector.select(null)}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#e6e8ee",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 11,
            padding: "1px 6px",
          }}
        >
          esc
        </button>
      </div>
      <div style={{ opacity: 0.7, marginTop: 2, fontSize: 11 }}>
        {selected.id ?? "—"} · {selected.kind ?? "(no kind)"}
      </div>
      <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.12)", margin: "6px 0" }} />
      <ComponentList entity={selected} />
    </div>
  );
}

function ComponentList({ entity }: { entity: Entity }) {
  const rows: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(entity)) {
    rows.push([k, formatValue(v)]);
  }
  return (
    <div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "flex", gap: 6 }}>
          <span style={{ color: "#7dd3fc", minWidth: 100 }}>{k}</span>
          <span style={{ opacity: 0.85, wordBreak: "break-word" }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  if (typeof v === "string") return JSON.stringify(v);
  if (typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    if ("container" in (v as object)) return "<sprite>";
    try {
      return JSON.stringify(v, (_k, val) => {
        if (typeof val === "number") return Math.round(val * 100) / 100;
        return val;
      });
    } catch {
      return String(v);
    }
  }
  return String(v);
}
